import { useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CreateCustomerRequest, CustomerDto } from '@/types';

interface CustomerFormModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCustomerRequest) => Promise<void> | void;
  editing?: CustomerDto | null;
}

interface FormValues {
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  pesel: string;
  notes: string;
}

const EMPTY: FormValues = {
  name: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  pesel: '',
  notes: '',
};

export default function CustomerFormModal({ show, onClose, onSubmit, editing }: CustomerFormModalProps) {
  const { t } = useTranslation();
  const { register, handleSubmit, reset, watch, formState: { isSubmitting } } = useForm<FormValues>({
    defaultValues: EMPTY,
  });
  const name = watch('name');

  useEffect(() => {
    if (!show) return;
    if (editing) {
      reset({
        name: editing.name,
        firstName: editing.firstName ?? '',
        lastName: editing.lastName ?? '',
        email: editing.email ?? '',
        phone: editing.phone ?? '',
        address: editing.address ?? '',
        pesel: editing.pesel ?? '',
        notes: editing.notes ?? '',
      });
    } else {
      reset(EMPTY);
    }
  }, [show, editing, reset]);

  const submit = handleSubmit(async (values) => {
    await onSubmit({
      name: values.name,
      firstName: values.firstName || undefined,
      lastName: values.lastName || undefined,
      email: values.email || undefined,
      phone: values.phone || undefined,
      address: values.address || undefined,
      pesel: values.pesel || undefined,
      notes: values.notes || undefined,
    });
    onClose();
  });

  return (
    <Modal show={show} onHide={onClose}>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? t('customers.editCustomer') : t('customers.addCustomer')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('customers.form.name')} *</Form.Label>
            <Form.Control {...register('name', { required: true, validate: (v) => v.trim().length > 0 })} />
          </Form.Group>
          <div className="row">
            <Form.Group className="mb-3 col-6">
              <Form.Label>{t('customers.form.firstName')}</Form.Label>
              <Form.Control {...register('firstName')} />
            </Form.Group>
            <Form.Group className="mb-3 col-6">
              <Form.Label>{t('customers.form.lastName')}</Form.Label>
              <Form.Control {...register('lastName')} />
            </Form.Group>
          </div>
          <div className="row">
            <Form.Group className="mb-3 col-6">
              <Form.Label>{t('customers.form.email')}</Form.Label>
              <Form.Control type="email" {...register('email')} />
            </Form.Group>
            <Form.Group className="mb-3 col-6">
              <Form.Label>{t('customers.form.phone')}</Form.Label>
              <Form.Control {...register('phone')} />
            </Form.Group>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>{t('customers.form.address')}</Form.Label>
            <Form.Control {...register('address')} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('customers.form.pesel')}</Form.Label>
            <Form.Control {...register('pesel')} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('customers.form.notes')}</Form.Label>
            <Form.Control as="textarea" rows={2} {...register('notes')} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>{t('confirm.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={isSubmitting || !name?.trim()}>
            {editing ? t('confirm.confirm') : t('customers.addCustomer')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
